// src/data/servicesData.js
const localServiceAreas = [
  'Reno',
  'Sparks',
  'Carson City',
  'Gardnerville',
  'Minden',
  'Lake Tahoe',
  'Spanish Springs',
  'Washoe County',
  'Douglas County'
];

const servicesData = [
  {
    title: 'Tree Pruning',
    subtitle: 'Enhancing Tree Health',
    description:
      'Professional pruning to maintain tree health and aesthetics, ensuring your landscape remains beautiful and safe.',
    buttonText: 'Learn More',
    buttonLink: '#pruning',
    slug: 'tree-pruning',
    fullDescription: `
## Understanding Professional Tree Pruning

Tree pruning is a critical aspect of landscape maintenance that goes far beyond simple aesthetic improvements. Our professional arborists understand that each cut can significantly impact a tree's health, structure, and longevity.

### The Science of Pruning

Proper pruning is a precise science that requires deep understanding of tree biology. Different tree species have unique pruning requirements, and our experts tailor their approach to each specific tree's needs.

### Key Pruning Objectives

We focus on strategic branch removal to:
- Remove dead, diseased, or crossing branches
- Improve tree health and structural integrity
- Enhance overall landscape design
- Prevent potential hazards
- Stimulate healthy growth
- Optimize air circulation and sunlight penetration

### Comprehensive Approach

Our approach combines advanced arboricultural techniques with a holistic understanding of tree health and landscape ecology. We don't just cut branches; we carefully assess and treat each tree as a unique living organism.`,
    features: [
      {
        title: 'Crown Thinning',
        description: 'Selective removal of branches to increase light penetration and air movement',
        icon: 'fas fa-spa'
      },
      {
        title: 'Dead Wood Removal',
        description: 'Elimination of dead, dying, and diseased branches',
        icon: 'fas fa-tree'
      },
      {
        title: 'Shape Maintenance',
        description: 'Careful pruning to maintain natural tree shape and structure',
        icon: 'fas fa-leaf'
      }
    ],
    benefits: [
      {
        title: 'Improved Tree Health',
        description: 'Proper pruning promotes better growth and reduces disease risk'
      },
      {
        title: 'Enhanced Safety',
        description: 'Removes hazardous branches that could pose risks'
      },
      {
        title: 'Better Aesthetics',
        description: 'Creates a more appealing and balanced tree appearance'
      }
    ],
    ctaText: 'Ready to enhance your trees\' health and beauty?',
    heroImage: './images/tree-pruning.jpg',
    tips: [
      {
        title: 'Seasonal Timing',
        description: 'The best time to prune most trees is during their dormant season (late winter to early spring). This minimizes stress and reduces the risk of disease transmission.',
        link: '/contact'
      },
      {
        title: 'Pruning Limits',
        description: "Never remove more than 25% of a tree's live branches in a single season. Over-pruning can stress the tree and compromise its health.",
        link: '/contact'
      },
      {
        title: 'Warning Signs',
        description: 'Watch for dead, diseased, or crossing branches. These can indicate underlying health issues that require professional assessment.',
        link: '/contact'
      },
      {
        title: 'Young Tree Training',
        description: 'Early pruning helps develop a strong structure, preventing future problems and reducing the need for major corrective pruning later.',
        link: '/contact'
      },
      {
        title: 'Tool Sterilization',
        description: 'Professional arborists always sterilize pruning tools between trees to prevent the spread of diseases and infections.',
        link: '/contact'
      },
      {
        title: 'Wound Protection',
        description: 'Large pruning cuts should be left unpainted. Modern arboriculture shows that trees heal best when cuts are made correctly and left to natural healing processes.',
        link: '/contact'
      }
    ],
    metadata: {
      pageTitle: 'Tree Pruning in Reno, Nevada | Professional Arborist Services',
      metaDescription: 'Expert tree pruning services in Northern Nevada. Professional arborists providing precise, health-focused pruning to enhance tree vitality and landscape beauty.',
      keywords: [
        'tree pruning Reno',
        'arborist tree care Nevada', 
        'professional tree trimming', 
        'Northern Nevada tree health'
      ],
      canonicalUrl: '/services/tree-pruning',
      serviceAreas: localServiceAreas
    }
  },
  {
    title: 'Tree Removal',
    subtitle: 'Safe & Efficient',
    description:
      'Expert tree removal services ensuring safety and minimal disruption to your property and surroundings.',
    buttonText: 'Learn More',
    buttonLink: '#removal',
    slug: 'tree-removal',
    fullDescription: `
## Understanding Professional Tree Removal

Tree removal is a complex and potentially dangerous process that requires professional expertise. Our comprehensive service is designed to address challenging situations while prioritizing safety, property protection, and environmental responsibility.

### When Tree Removal Becomes Necessary

Professional tree removal is crucial in several critical scenarios:
- Trees severely damaged by storms or disease
- Trees posing immediate risk to structures or people
- Trees interfering with construction or infrastructure
- Trees with irreparable structural damage

### Our Removal Process

We approach tree removal with a meticulous and safety-focused methodology:
- Comprehensive site assessment
- Precise removal techniques
- Advanced equipment and safety protocols
- Minimal impact on surrounding landscape
- Complete debris removal and site cleanup

### Environmental Responsibility

We understand that tree removal is more than just cutting down a tree. It's about protecting your property, ensuring safety, and maintaining the ecological balance of your landscape.`,
    features: [
      {
        title: 'Safe Removal',
        description: 'Advanced equipment and techniques for secure tree removal',
        icon: 'fas fa-hard-hat'
      },
      {
        title: 'Property Protection',
        description: 'Comprehensive measures to protect surrounding structures',
        icon: 'fas fa-shield-alt'
      },
      {
        title: 'Complete Cleanup',
        description: 'Thorough site cleanup after removal',
        icon: 'fas fa-broom'
      }
    ],
    benefits: [
      {
        title: 'Risk Elimination',
        description: 'Removes hazardous trees that threaten property'
      },
      {
        title: 'Space Creation',
        description: 'Creates space for new landscaping or construction'
      },
      {
        title: 'Property Value',
        description: 'Improves property aesthetics and safety'
      }
    ],
    tips: [
      {
        title: 'Professional Assessment',
        description: 'Always consult professionals before attempting tree removal. Hidden risks can be dangerous and lead to property damage.',
        link: '/contact'
      },
      {
        title: 'Permit Requirements',
        description: 'Check local regulations. Many municipalities require specific permits for tree removal, especially for protected or heritage trees.',
        link: '/contact'
      },
      {
        title: 'Consider Alternatives',
        description: "Sometimes pruning or treatment can save a tree. Our experts can provide a comprehensive assessment of the tree's health.",
        link: '/contact'
      },
      {
        title: 'Timing Matters',
        description: 'Some seasons are better for tree removal. Factors like tree species, local climate, and surrounding landscape play crucial roles.',
        link: '/contact'
      },
      {
        title: 'Safety First',
        description: "Never attempt to remove large trees without professional help. Improper removal can cause serious injury or property damage.",
        link: '/contact'
      },
      {
        title: 'Post-Removal Planning',
        description: "Consider your landscape's future. We can help you plan for replanting or redesigning the area after tree removal.",
        link: '/contact'
      }
    ],
    ctaText: 'Need expert tree removal services?',
    heroImage: './images/tree-removal.jpg',
    metadata: {
      pageTitle: 'Tree Removal Services in Reno, Nevada | Safe Removal',
      metaDescription: 'Professional tree removal services in Northern Nevada. Safe, efficient removal by certified arborists, minimizing property disruption.',
      keywords: [
        'tree removal Reno',
        'professional tree cutting',
        'Nevada arborist removal services',
        'safe tree removal'
      ],
      canonicalUrl: '/services/tree-removal',
      serviceAreas: localServiceAreas
    }
  },
  {
    title: 'Stump Grinding',
    subtitle: 'Complete Removal',
    description:
      'Effective stump grinding to restore your landscape seamlessly, preventing future growth and enhancing beauty.',
    buttonText: 'Learn More',
    buttonLink: '#stump',
    slug: 'stump-grinding',
    fullDescription: `
## Understanding Stump Grinding

Stump grinding is a critical service that goes far beyond simple landscape maintenance. Leftover tree stumps can create numerous challenges for property owners, from aesthetic issues to potential safety hazards.

### Why Remove Tree Stumps?

Tree stumps can:
- Attract unwanted pests and insects
- Create tripping hazards
- Inhibit new landscape development
- Potentially spread tree diseases
- Detract from your property's overall appearance
- Interfere with lawn maintenance

### Our Professional Approach

We use state-of-the-art grinding equipment designed to:
- Remove stumps completely below ground level
- Minimize landscape disruption
- Prepare the area for future landscaping
- Ensure thorough root system elimination

### Environmental Considerations

Our stump grinding process is environmentally responsible, focusing on minimal impact and maximum efficiency.`,
    features: [
      {
        title: 'Deep Grinding',
        description: 'Remove stumps well below ground level',
        icon: 'fas fa-cog'
      },
      {
        title: 'Site Restoration',
        description: 'Complete cleanup and soil preparation',
        icon: 'fas fa-seedling'
      },
      {
        title: 'Growth Prevention',
        description: 'Techniques to prevent regrowth',
        icon: 'fas fa-ban'
      }
    ],
    benefits: [
      {
        title: 'Landscape Beauty',
        description: 'Restores the aesthetic appeal of your property'
      },
      {
        title: 'Safety',
        description: 'Eliminates tripping hazards and pest habitats'
      },
      {
        title: 'New Opportunities',
        description: 'Creates space for new landscaping projects'
      }
    ],
    tips: [
      {
        title: 'Depth Matters',
        description: 'Grinding 4-6 inches below ground ensures complete removal and prevents potential regrowth.',
        link: '/contact'
      },
      {
        title: 'Soil Preparation',
        description: 'After grinding, we recommend adding fresh soil and considering replanting to restore the landscape.',
        link: '/contact'
      },
      {
        title: 'Equipment Expertise',
        description: 'Different stump types require specialized grinding approaches. Our experts select the most appropriate technique.',
        link: '/contact'
      },
      {
        title: 'Root System Management',
        description: 'Complete stump removal includes addressing the entire root system to prevent future issues.',
        link: '/contact'
      },
      {
        title: 'Landscape Impact',
        description: "Stump grinding can significantly improve your property's usability and aesthetic appeal.",
        link: '/contact'
      },
      {
        title: 'Environmental Responsibility',
        description: 'We ensure minimal environmental disruption during the stump removal process.',
        link: '/contact'
      }
    ],
    ctaText: 'Ready to remove that unsightly stump?',
    heroImage: './images/stump-grinding.jpg',
    metadata: {
      pageTitle: 'Stump Grinding Services in Reno, Nevada | Complete Removal',
      metaDescription: 'Professional stump grinding services in Northern Nevada. Comprehensive stump removal to restore your landscape and prevent future growth.',
      keywords: [
        'stump grinding Reno',
        'stump removal Nevada',
        'landscape restoration',
        'professional stump services'
      ],
      canonicalUrl: '/services/stump-grinding',
      serviceAreas: localServiceAreas
    }
  },
  {
    title: 'Emergency Services',
    subtitle: '24/7 Support',
    description:
      'Reliable emergency tree services available around the clock, ensuring your property remains safe in any situation.',
    buttonText: 'Learn More',
    buttonLink: '#emergency',
    slug: 'emergency-services',
    fullDescription: `
## 24/7 Emergency Tree Services

When nature strikes unexpectedly, our emergency tree services are your first line of defense. We understand that tree-related emergencies can occur at any time, posing significant risks to your property and safety.

### What Constitutes an Emergency?

Tree emergencies can include:
- Storm-damaged trees
- Fallen trees blocking roads or structures
- Trees threatening power lines
- Dangerous hanging branches
- Immediate risk to buildings or people
- Sudden tree collapse

### Our Rapid Response Methodology

Our emergency team is:
- Available 24/7
- Equipped with advanced tools
- Trained in high-stress situations
- Focused on immediate risk mitigation
- Committed to swift, safe resolution

### Safety and Expertise

We prioritize human safety and property protection in every emergency scenario.`,
    features: [
      {
        title: '24/7 Response',
        description: 'Round-the-clock emergency support',
        icon: 'fas fa-clock'
      },
      {
        title: 'Rapid Deployment',
        description: 'Quick response to emergency situations',
        icon: 'fas fa-bolt'
      },
      {
        title: 'Storm Damage',
        description: 'Expert handling of storm-damaged trees',
        icon: 'fas fa-cloud-showers-heavy'
      }
    ],
    benefits: [
      {
        title: 'Immediate Safety',
        description: 'Quick response to prevent further damage'
      },
      {
        title: 'Peace of Mind',
        description: '24/7 availability for emergencies'
      },
      {
        title: 'Professional Care',
        description: 'Expert handling of emergency situations'
      }
    ],
    tips: [
      {
        title: 'Immediate Safety',
        description: 'Keep a safe distance from damaged trees. Do not attempt to remove them yourself.',
        link: '/contact'
      },
      {
        title: 'Quick Reporting',
        description: 'Report emergency tree situations immediately to minimize potential damage.',
        link: '/contact'
      },
      {
        title: 'Documentation',
        description: 'If safe, take photos of the damage for insurance and assessment purposes.',
        link: '/contact'
      },
      {
        title: 'Utility Awareness',
        description: 'If trees are near power lines, contact utility companies immediately.',
        link: '/contact'
      },
      {
        title: 'Insurance Considerations',
        description: 'Many homeowner insurance policies cover emergency tree removal. Check your policy.',
        link: '/contact'
      },
      {
        title: 'Preventive Maintenance',
        description: 'Regular tree health assessments can prevent many emergency situations.',
        link: '/contact'
      }
    ],
    ctaText: 'Having an emergency? Contact us now!',
    heroImage: './images/emergency-services.jpg',
    metadata: {
      pageTitle: '24/7 Emergency Tree Services in Reno, Nevada',
      metaDescription: 'Immediate emergency tree services in Northern Nevada. Rapid response to storm damage, fallen trees, and urgent tree-related safety concerns.',
      keywords: [
        'emergency tree services',
        'storm damage tree removal',
        '24/7 tree emergency Reno',
        'urgent tree care'
      ],
      canonicalUrl: '/services/emergency-services',
      serviceAreas: localServiceAreas
    }
  },
  {
    title: 'Tree Health',
    subtitle: 'Comprehensive Evaluation',
    description:
      'Detailed assessments to identify and treat tree diseases, promoting long-term vitality and growth.',
    buttonText: 'Learn More',
    buttonLink: '#health-assessment',
    slug: 'tree-health',
    fullDescription: `
## Comprehensive Tree Health Assessment

Tree health is a complex science that requires expert knowledge, advanced diagnostic techniques, and a holistic approach to landscape management.

### Why Tree Health Matters

Healthy trees:
- Enhance property value
- Provide environmental benefits
- Prevent potential safety hazards
- Support local ecosystem
- Improve air quality
- Create aesthetic beauty

### Our Diagnostic Process

We employ advanced techniques to:
- Identify potential diseases
- Assess structural integrity
- Evaluate nutrient requirements
- Detect pest infestations
- Recommend targeted treatments

### Holistic Tree Care

Our approach goes beyond simple diagnosis, focusing on long-term tree vitality and landscape health.`,
    features: [
      {
        title: 'Health Assessment',
        description: 'Thorough evaluation of tree condition',
        icon: 'fas fa-clipboard-check'
      },
      {
        title: 'Disease Treatment',
        description: 'Expert treatment of tree diseases',
        icon: 'fas fa-prescription-bottle-alt'
      },
      {
        title: 'Preventive Care',
        description: 'Ongoing maintenance for optimal health',
        icon: 'fas fa-heart'
      }
    ],
    benefits: [
      {
        title: 'Early Detection',
        description: 'Identify problems before they become severe'
      },
      {
        title: 'Longer Life',
        description: 'Extend the lifespan of your trees'
      },
      {
        title: 'Property Value',
        description: 'Maintain landscape value with healthy trees'
      }
    ],
    tips: [
      {
        title: 'Early Detection',
        description: 'Regular health assessments can catch potential issues before they become critical.',
        link: '/contact'
      },
      {
        title: 'Seasonal Considerations',
        description: 'Different seasons require different tree care strategies.',
        link: '/contact'
      },
      {
        title: 'Soil Health',
        description: 'Tree health starts with soil quality. We analyze and improve soil conditions.',
        link: '/contact'
      },
      {
        title: 'Pest Management',
        description: 'Proactive pest control is crucial for maintaining tree health.',
        link: '/contact'
      },
      {
        title: 'Nutrient Balance',
        description: 'Proper fertilization and nutrient management support tree longevity.',
        link: '/contact'
      },
      {
        title: 'Professional Intervention',
        description: 'Some tree health issues require specialized professional treatment.',
        link: '/contact'
      }
    ],
    ctaText: 'Want to ensure your trees stay healthy?',
    heroImage: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09',
    metadata: {
      pageTitle: 'Tree Health Assessment in Reno, Nevada | Expert Diagnostics',
      metaDescription: 'Comprehensive tree health assessments in Northern Nevada. Professional diagnosis, disease treatment, and preventive care for your trees.',
      keywords: [
        'tree health assessment',
        'tree disease treatment Reno',
        'arborist health evaluation',
        'Northern Nevada tree care'
      ],
      canonicalUrl: '/services/tree-health',
      serviceAreas: localServiceAreas
    }
  },
  {
    title: 'Land Clearing',
    subtitle: 'Efficient Clearing',
    description:
      'Complete land clearing services to prepare your property for new projects, ensuring a clean and safe environment.',
    buttonText: 'Learn More',
    buttonLink: '#land-clearing',
    slug: 'land-clearing',
    fullDescription: `
## Professional Land Clearing Services

Land clearing is a critical first step in property development, landscaping, and environmental management. Our comprehensive approach ensures efficient, responsible land transformation.

### Why Professional Land Clearing?

Professional clearing provides:
- Precise site preparation
- Environmental conservation
- Compliance with local regulations
- Minimal ecological disruption
- Enhanced property usability
- Improved land value

### Our Clearing Methodology

We employ advanced techniques to:
- Assess environmental impact
- Remove vegetation strategically
- Preserve valuable landscape features
- Prepare sites for development
- Manage debris responsibly

### Sustainable Practices

We prioritize environmentally responsible land clearing techniques.`,
    features: [
      {
        title: 'Site Preparation',
        description: 'Complete property clearing and preparation',
        icon: 'fas fa-truck-pickup'
      },
      {
        title: 'Debris Removal',
        description: 'Thorough cleanup and disposal services',
        icon: 'fas fa-recycle'
      },
      {
        title: 'Environmental Care',
        description: 'Eco-friendly clearing methods',
        icon: 'fas fa-globe-americas'
      }
    ],
    benefits: [
      {
        title: 'Ready for Development',
        description: 'Prepare land for new construction or landscaping'
      },
      {
        title: 'Clean Slate',
        description: 'Complete removal of unwanted vegetation'
      },
      {
        title: 'Compliance',
        description: 'Meet all local clearing regulations'
      }
    ],
    tips: [
      {
        title: 'Permit Awareness',
        description: 'Always check local regulations before beginning land clearing projects.',
        link: '/contact'
      },
      {
        title: 'Environmental Assessment',
        description: 'Conduct thorough environmental assessments before clearing.',
        link: '/contact'
      },
      {
        title: 'Selective Clearing',
        description: 'Not all vegetation needs removal. Strategic preservation can enhance property value.',
        link: '/contact'
      },
      {
        title: 'Erosion Prevention',
        description: 'Proper land clearing techniques help prevent soil erosion.',
        link: '/contact'
      },
      {
        title: 'Debris Management',
        description: 'Responsible disposal and potential recycling of cleared materials.',
        link: '/contact'
      },
      {
        title: 'Future Planning',
        description: 'Consider long-term land use when planning clearing projects.',
        link: '/contact'
      }
    ],
    ctaText: 'Ready to clear your land for new opportunities?',
    heroImage: 'https://images.unsplash.com/photo-1499529112087-3cb3b73cec95',
    metadata: {
      pageTitle: 'Land Clearing Services in Reno, Nevada | Professional Clearing',
      metaDescription: 'Expert land clearing services in Northern Nevada. Efficient, environmentally responsible property preparation for development and landscaping.',
      keywords: [
        'land clearing Reno',
        'property preparation',
        'Nevada land clearing services',
        'environmental land management'
      ],
      canonicalUrl: '/services/land-clearing',
      serviceAreas: localServiceAreas
    }
  }
];

module.exports = {
  localServiceAreas,
  servicesData,
  getServiceBySlug: (slug) => servicesData.find(service => service.slug === slug)
};